// MentionUserSheet.js
import { useState, useEffect } from "react";
import { useSystemMessage, SYSTEM_EVENTS } from "../hooks/useSystemMessage";
import { toast } from "react-toastify";
/* ─────────────────────────────────────────────────────────────────────────────
   MentionUserSheet
   WhatsApp-style bottom sheet shown when tapping an @mention in a group chat.

   Props
   ─────
   open             – boolean, controls visibility
   onClose          – () => void
   mentionedUser    – merged user+member object (chatId, userName, isAdmin,
                      isSuperAdmin, online, profileImageUrl, avatarUrl …)
   myChatId         – current viewer's chatId (string)
   myName           – current viewer's display name (string) — used as actorName
                      in system messages so other members see the real name
   isViewerAdmin    – boolean: is the current viewer an admin of this group?
   mutualGroups     – array of { groupId, name } shown at the bottom
   onOpenGroup      – (group) => void  — navigate to a mutual group
   socket           – socket.io socket instance
   groupId          – current group's groupId
   onOpenDirectMessage – (item) => void
                      Called when the viewer taps "Send Message".
                      Receives an item shaped like:
                        { type: "user", chatId, username }
─────────────────────────────────────────────────────────────────────────────── */
export function MentionUserSheet({
  open,
  onClose,
  mentionedUser,
  myChatId,
  myName,           // ← NEW: real display name of the current user
  isViewerAdmin,
  mutualGroups,
  onOpenGroup,
  socket,
  groupId,
  onOpenDirectMessage,
  setMessages,
  setGroups,
  getGroupKey,
  uuid,
}) {
  const [visible, setVisible] = useState(false);

  // ── System message hook ───────────────────────────────────────────
  const { sendSystemMessage } = useSystemMessage({
    socket,
    myChatId,
    myName,           // ← passed through so the hook can localise "You"
    setMessages,
    setGroups,
    getGroupKey,
    uuid,
  });

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  if (!open && !visible) return null;

  /* ── Derived display values ─────────────────────────────────────────────── */
  const isMe = String(mentionedUser?.chatId) === String(myChatId);

  const COLOURS = [
    "#e57373","#f06292","#ba68c8","#7986cb",
    "#64b5f6","#4db6ac","#81c784","#ffb74d","#ff8a65",
  ];
  const displayName =
    mentionedUser?.userName  ||
    mentionedUser?.username  ||
    mentionedUser?.name      ||
    "Unknown";

  const avatarColour = COLOURS[(displayName.charCodeAt(0) || 0) % COLOURS.length];
  const avatarUrl    = mentionedUser?.profileImageUrl || mentionedUser?.avatarUrl || null;
  const isOnline     = mentionedUser?.online;

  // The real name of the acting admin — used in encrypted payload so other
  // members see e.g. "James made Francis an admin".
  // The hook will replace this with "You" only in the actor's own optimistic bubble.
  const actorName = myName || "Admin";

  /* ── Role checks ────────────────────────────────────────────────────────── */
  const isMemberAdmin      = !!(mentionedUser?.isAdmin || mentionedUser?.isSuperAdmin);
  const isMemberSuperAdmin = !!mentionedUser?.isSuperAdmin;
  const canRemove          = isViewerAdmin && !isMe && !isMemberSuperAdmin;
  const canPromote         = isViewerAdmin && !isMe && !isMemberAdmin;
  const canDemote          = isViewerAdmin && !isMe && isMemberAdmin && !isMemberSuperAdmin;

  /* ── Handlers ───────────────────────────────────────────────────────────── */

  /**
   * "Send Message" — opens a DM with the mentioned user.
   */
  const handleSendMessage = () => {
    if (!mentionedUser?.chatId) return;
    onOpenDirectMessage?.({
      type:     "user",
      chatId:   mentionedUser.chatId,
      username:
        mentionedUser.userName ||
        mentionedUser.username ||
        mentionedUser.name     ||
        String(mentionedUser.chatId),
    });
    onClose();
  };

  const handleMakeAdmin = async () => {
    if (!socket || !groupId || !mentionedUser?.chatId) return;

    socket.emit("group-make-admin", {
      groupId,
      adminChatId: myChatId,
      targetChatId: mentionedUser.chatId,
    });

    // actorName is the real name — other members decrypt and see e.g. "James made Francis an admin"
    // The hook substitutes "You" in the local optimistic bubble only
    await sendSystemMessage({
      groupId,
      type:         SYSTEM_EVENTS.ADMIN_PROMOTED,
      actorName,
      targetName:   displayName,
      actorChatId:  myChatId,
      targetChatId: mentionedUser.chatId,
    });
toast.success("User promoted to admin"); 
    onClose();
  };

  const handleDemoteAdmin = async () => {
    if (!socket || !groupId || !mentionedUser?.chatId) return;

    socket.emit("group-demote-admin", {
      groupId,
      adminChatId: myChatId,
      targetChatId: mentionedUser.chatId,
    });

    await sendSystemMessage({
      groupId,
      type:         SYSTEM_EVENTS.ADMIN_DEMOTED,
      actorName,
      targetName:   displayName,
      actorChatId:  myChatId,
      targetChatId: mentionedUser.chatId,
    });
toast.info("Admin dismissed");
    onClose();
  };

  const handleRemoveFromGroup = async () => {
    if (!socket || !groupId || !mentionedUser?.chatId) return;

    socket.emit("group-remove-member", {
      groupId,
      adminChatId: myChatId,
      removeChatId: mentionedUser.chatId,
    });

    await sendSystemMessage({
      groupId,
      type:         SYSTEM_EVENTS.MEMBER_REMOVED,
      actorName,
      targetName:   displayName,
      actorChatId:  myChatId,
      targetChatId: mentionedUser.chatId,
    });
toast.info("Member removed"); 
    onClose();
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div
      className={[
        "fixed inset-0 z-50 flex items-end justify-center transition-all duration-300",
        visible && open ? "bg-black/50" : "bg-transparent pointer-events-none",
      ].join(" ")}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={[
          "w-full max-w-lg bg-white dark:bg-neutral-900 rounded-t-2xl shadow-2xl",
          "transition-transform duration-300 ease-out",
          visible && open ? "translate-y-0" : "translate-y-full",
        ].join(" ")}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-neutral-300 dark:bg-neutral-600" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-2 pb-3 border-b border-neutral-100 dark:border-neutral-800">
          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            User Info
          </h3>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Avatar + name + online status */}
        <div className="flex flex-col items-center px-6 py-5">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden text-white text-3xl font-bold ring-4 ring-white dark:ring-neutral-900 shadow-lg"
            style={{ backgroundColor: avatarColour }}
          >
            {avatarUrl
              ? <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
              : <span>{displayName.charAt(0).toUpperCase()}</span>
            }
          </div>

          <div className="mt-3 text-xl font-bold text-neutral-900 dark:text-neutral-100">
            {displayName}
          </div>

          {/* Role badge */}
          {isMemberSuperAdmin && (
            <span className="mt-1 text-xs font-medium text-red-500">Super Admin</span>
          )}
          {!isMemberSuperAdmin && isMemberAdmin && (
            <span className="mt-1 text-xs font-medium text-blue-500">Admin</span>
          )}

          <div className="mt-1 flex items-center gap-1.5 text-sm">
            <span
              className={[
                "w-2 h-2 rounded-full flex-shrink-0",
                isOnline ? "bg-emerald-500" : "bg-neutral-400",
              ].join(" ")}
            />
            <span
              className={
                isOnline
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-neutral-500 dark:text-neutral-400"
              }
            >
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 pb-3 border-t border-neutral-100 dark:border-neutral-800">
          <div className="flex flex-col gap-2 mt-3">

            {/* Send Message — always shown except when viewing yourself */}
            {!isMe && (
              <button
                onClick={handleSendMessage}
                className="w-full px-4 py-3 rounded-xl bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 text-sm font-medium text-green-700 dark:text-green-400 text-left active:scale-[0.98] transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                💬 Send Message
              </button>
            )}

            {/* Make Admin */}
            {canPromote && (
              <button
                onClick={handleMakeAdmin}
                className="w-full px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-sm font-medium text-blue-700 dark:text-blue-400 text-left active:scale-[0.98] transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                ⭐ Make Group Admin
              </button>
            )}

            {/* Dismiss Admin */}
            {canDemote && (
              <button
                onClick={handleDemoteAdmin}
                className="w-full px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-sm font-medium text-amber-700 dark:text-amber-400 text-left active:scale-[0.98] transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                🔽 Dismiss as Admin
              </button>
            )}

            {/* Remove from Group */}
            {canRemove && (
              <button
                onClick={handleRemoveFromGroup}
                className="w-full px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-sm font-medium text-red-600 dark:text-red-400 text-left active:scale-[0.98] transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                🚫 Remove from Group
              </button>
            )}
          </div>
        </div>

        {/* Mutual groups */}
        {mutualGroups && mutualGroups.length > 0 && (
          <div className="px-4 pb-3 border-t border-neutral-100 dark:border-neutral-800">
            <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mt-3 mb-2">
              Groups in common
            </div>
            <div className="flex flex-col gap-1.5">
              {mutualGroups.map((g) => (
                <button
                  key={g.groupId}
                  onClick={() => { onOpenGroup?.(g); onClose(); }}
                  className="w-full px-3 py-2.5 rounded-xl bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-sm text-left font-medium text-neutral-800 dark:text-neutral-200 active:scale-[0.98] transition-all touch-manipulation"
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="h-2" />
      </div>
    </div>
  );
}

export default MentionUserSheet;